import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ConflictError } from '../api/client';
import type { EmployeeRosterRow, ScheduleDayView } from '../api/types';
import { useApp } from '../state/AppContext';
import { ConflictBanner, EmptyRow, ErrorBanner, LocationSelect, SortableHeader } from '../components/ui';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;
type SortColumn = 'displayName' | 'department' | 'locationCode' | 'summary';

interface Draft {
  employeeId: number;
  scheduleVersion: number;
  department: string;
  locationCode: string;
  days: Record<string, { start: string; end: string }>;
}

/**
 * Roster and weekly schedule management.
 *
 * These times are what Late and Unscheduled-absence flags are measured
 * against, so an inaccurate schedule shows up directly as a wrong flag on
 * Daily Summary. Changes apply going forward — regenerating daily records
 * never overturns a decision a reviewer already made.
 */
export function Schedules() {
  const { reference, scopedDepartments, scopedFacilities, can, locationsFor } = useApp();
  const queryClient = useQueryClient();
  const canEdit = can('schedules', 'edit');

  const [search, setSearch] = useState('');
  const [location, setLocation] = useState('all');
  const [department, setDepartment] = useState('all');
  const [showAll, setShowAll] = useState(false);
  const [sort, setSort] = useState<{ column: SortColumn; dir: 'asc' | 'desc' }>({ column: 'displayName', dir: 'asc' });
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [draft, setDraft] = useState<Draft | null>(null);
  const editPanelRef = useRef<HTMLDivElement>(null);
  const bulkPanelRef = useRef<HTMLDivElement>(null);
  const [bulkDays, setBulkDays] = useState<Record<string, { start: string; end: string }>>(
    { Mon: { start: '', end: '' }, Tue: { start: '', end: '' }, Wed: { start: '', end: '' }, Thu: { start: '', end: '' }, Fri: { start: '', end: '' }, Sat: { start: '', end: '' }, Sun: { start: '', end: '' } });
  const [bulkResult, setBulkResult] = useState<string | null>(null);

  // The roster runs to a hundred-plus rows, so an edit panel that simply
  // appears somewhere on the page reads as a dead click. Bring it into view.
  useEffect(() => {
    if (draft) editPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [draft?.employeeId]);

  useEffect(() => {
    if (selected.size > 0) bulkPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [selected.size > 0]);
  const [conflict, setConflict] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [removed, setRemoved] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['roster', search, location, department, showAll],
    queryFn: () => api.get<{ rows: EmployeeRosterRow[]; totalCount: number; outOfScopeHidden: number }>(
      '/api/employees', { search, location, department, take: 1000, scopeOnly: !showAll }),
  });

  const rows = useMemo(() => {
    const list = [...(data?.rows ?? [])];
    const dir = sort.dir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      switch (sort.column) {
        case 'department': return (a.department ?? '').localeCompare(b.department ?? '') * dir;
        case 'locationCode': return (a.locationCode ?? '').localeCompare(b.locationCode ?? '') * dir;
        case 'summary': return a.summary.localeCompare(b.summary) * dir;
        default: return a.sortName.localeCompare(b.sortName) * dir;
      }
    });
    return list;
  }, [data, sort]);

  // One request rather than a PUT per employee — a browser-side loop would be
  // the same per-row round trip that made imports take minutes.
  const bulkSave = useMutation({
    mutationFn: (ids: number[]) =>
      api.put<{ applied: number; requested: number }>('/api/schedules/bulk', {
        employeeIds: ids,
        mon: bulkDays.Mon, tue: bulkDays.Tue, wed: bulkDays.Wed, thu: bulkDays.Thu,
        fri: bulkDays.Fri, sat: bulkDays.Sat, sun: bulkDays.Sun,
      }),
    onSuccess: (r) => {
      setSelected(new Set());
      setBulkResult(`Applied to ${r.applied} ${r.applied === 1 ? 'employee' : 'employees'}.`);
      queryClient.invalidateQueries();
      setTimeout(() => setBulkResult(null), 6000);
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Bulk apply failed.'),
  });

  const removeSchedule = useMutation({
    mutationFn: (employeeId: number) =>
      api.del<{ dailyRecordsRemoved: number }>(`/api/schedules/${employeeId}`),
    onSuccess: (r) => {
      setDraft(null);
      queryClient.invalidateQueries();
      setError(null);
      setRemoved(`Schedule removed${r.dailyRecordsRemoved ? ` — ${r.dailyRecordsRemoved} daily records cleared` : ''}.`);
      setTimeout(() => setRemoved(null), 6000);
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not remove the schedule.'),
  });

  const save = useMutation({
    mutationFn: (d: Draft) =>
      api.put(`/api/schedules/${d.employeeId}`, {
        employeeId: d.employeeId,
        rowVersionNo: d.scheduleVersion,
        mon: pair(d, 'Mon'), tue: pair(d, 'Tue'), wed: pair(d, 'Wed'), thu: pair(d, 'Thu'),
        fri: pair(d, 'Fri'), sat: pair(d, 'Sat'), sun: pair(d, 'Sun'),
        department: d.department || null,
        locationCode: d.locationCode || null,
      }),
    onSuccess: () => {
      setDraft(null);
      setConflict(null);
      queryClient.invalidateQueries({ queryKey: ['roster'] });
    },
    onError: (e) => (e instanceof ConflictError ? setConflict(e.message) : setError(e.message)),
  });

  const openEdit = (r: EmployeeRosterRow) =>
    setDraft({
      employeeId: r.employeeId,
      scheduleVersion: r.scheduleVersion,
      department: r.department ?? '',
      locationCode: r.locationCode ?? '',
      days: Object.fromEntries(
        DAYS.map((d) => {
          const found = r.days.find((x: ScheduleDayView) => x.day === d);
          return [d, { start: found?.start ?? '', end: found?.end ?? '' }];
        }),
      ),
    });

  /** Most schedules are near-identical; typing seven rows from scratch is the
   *  thing this screen replaces. */
  const copyFrom = (sourceId: number) => {
    const source = rows.find((r) => r.employeeId === sourceId);
    if (!source || !draft) return;
    setDraft({
      ...draft,
      days: Object.fromEntries(
        DAYS.map((d) => {
          const found = source.days.find((x) => x.day === d);
          return [d, { start: found?.start ?? '', end: found?.end ?? '' }];
        }),
      ),
    });
  };

  const toggleSort = (column: string) =>
    setSort((s) => ({ column: column as SortColumn, dir: s.column === column && s.dir === 'asc' ? 'desc' : 'asc' }));

  const editing = draft ? rows.find((r) => r.employeeId === draft.employeeId) : null;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Employee Schedules</div>
          <div className="page-subtitle">
            View and edit employee details and weekly schedules. Late and absence flags are measured against these
            times; changes apply going forward and do not alter past scoring.
          </div>
        </div>
      </div>

      {conflict && <ConflictBanner message={conflict} onReload={() => { setConflict(null); queryClient.invalidateQueries({ queryKey: ['roster'] }); }} />}
      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      {removed && <div className="banner banner--info">{removed}</div>}

      <div className="filter-row">
        <input className="control" style={{ flex: 1, maxWidth: 320 }} placeholder="Search name or employee ID"
          value={search} onChange={(e) => setSearch(e.target.value)} />
        <select className="control" style={{ maxWidth: 220 }} value={department} onChange={(e) => setDepartment(e.target.value)}>
          <option value="all">All departments</option>
          <option value="__none__">— No department set —</option>
          {(showAll ? reference.departments : scopedDepartments).map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <LocationSelect facilities={showAll ? reference.facilities : scopedFacilities} allowed={locationsFor('schedules', 'view')} value={location} onChange={setLocation} />
        <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, color: 'var(--text-secondary)' }}>
          <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
          Show everyone
          {(data?.outOfScopeHidden ?? 0) > 0 && !showAll && (
            <span className="meta">({(data?.outOfScopeHidden ?? 0)} outside scorecard settings)</span>
          )}
        </label>
        <div className="meta">{rows.length} of {data?.totalCount ?? 0} employees</div>
      </div>

      {bulkResult && <div className="banner banner--info">{bulkResult}</div>}

      {selected.size > 0 && canEdit && (
        <div ref={bulkPanelRef} className="card" style={{ marginBottom: 18, borderColor: 'var(--blue-border)' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
            <div className="section-label" style={{ marginBottom: 0 }}>
              APPLY ONE SCHEDULE TO {selected.size} {selected.size === 1 ? 'EMPLOYEE' : 'EMPLOYEES'}
            </div>
            <button className="btn btn--sm" style={{ border: 'none', background: 'transparent', color: 'var(--note)' }}
              onClick={() => setSelected(new Set())}>
              Deselect all
            </button>
          </div>

          <div className="meta" style={{ marginBottom: 12 }}>
            These times <strong>replace</strong> whatever those employees have now, including any schedule already set.
            Leave a day blank to mark it not scheduled. Copy an existing schedule below to avoid typing it out.
          </div>

          <div style={{ maxWidth: 380, marginBottom: 12 }}>
            <label className="field-label">START FROM AN EXISTING SCHEDULE</label>
            <select className="control" style={{ width: '100%' }} value=""
              onChange={(e) => {
                const src = rows.find((r) => r.employeeId === Number(e.target.value));
                if (!src) return;
                setBulkDays(Object.fromEntries(DAYS.map((day) => {
                  const f = src.days.find((x) => x.day === day);
                  return [day, { start: f?.start ?? '', end: f?.end ?? '' }];
                })));
              }}>
              <option value="">Copy from…</option>
              {rows.filter((r) => r.hasSchedule).map((r) => (
                <option key={r.employeeId} value={r.employeeId}>{r.displayName} — {r.summary}</option>
              ))}
            </select>
          </div>

          <div style={{ border: '1px solid var(--rule)', borderRadius: 8, overflow: 'hidden', marginBottom: 14 }}>
            {DAYS.map((day) => {
              const v = bulkDays[day];
              const off = !v.start && !v.end;
              return (
                <div key={day} style={{
                  display: 'grid', gridTemplateColumns: '64px 1fr 1fr 1.1fr 70px', gap: 12,
                  alignItems: 'center', padding: '9px 16px', borderBottom: '1px solid var(--rule)',
                }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700 }}>{day}</div>
                  <input className="control" placeholder="Start" value={v.start}
                    onChange={(e) => setBulkDays({ ...bulkDays, [day]: { ...v, start: e.target.value } })} />
                  <input className="control" placeholder="End" value={v.end}
                    onChange={(e) => setBulkDays({ ...bulkDays, [day]: { ...v, end: e.target.value } })} />
                  <div style={{ fontSize: 11.5, color: 'var(--orange)', fontWeight: 600 }}>{off ? 'Not scheduled' : ''}</div>
                  <div style={{ textAlign: 'right' }}>
                    <button className="btn btn--sm" style={{ border: 'none', background: 'transparent', color: 'var(--note)' }}
                      onClick={() => setBulkDays({ ...bulkDays, [day]: { start: '', end: '' } })}>
                      Clear
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button className="btn btn--primary" disabled={bulkSave.isPending}
              onClick={() => bulkSave.mutate([...selected])}>
              {bulkSave.isPending ? 'Applying…' : `Apply to ${selected.size} ${selected.size === 1 ? 'employee' : 'employees'}`}
            </button>
            <button className="btn" onClick={() => setBulkDays({ Mon: { start: '', end: '' }, Tue: { start: '', end: '' }, Wed: { start: '', end: '' }, Thu: { start: '', end: '' }, Fri: { start: '', end: '' }, Sat: { start: '', end: '' }, Sun: { start: '', end: '' } })}>Clear all days</button>
            <div className="meta" style={{ marginLeft: 'auto' }}>Times accept “7:00 AM” or “07:00”.</div>
          </div>
        </div>
      )}

      {draft && editing && (
        <div ref={editPanelRef} className="card" style={{ marginBottom: 18, borderColor: 'var(--input-border)' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 18 }}>
            <div className="section-label" style={{ marginBottom: 0 }}>
              EDIT — {editing.displayName} ({editing.employeeId})
            </div>
            <button className="btn btn--sm" style={{ border: 'none', background: 'transparent', color: 'var(--note)' }}
              onClick={() => setDraft(null)}>
              Close
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 22 }}>
            <div>
              <label className="field-label">DEPARTMENT</label>
              <select className="control" style={{ width: '100%' }} value={draft.department}
                onChange={(e) => setDraft({ ...draft, department: e.target.value })}>
                <option value="">—</option>
                {reference.departments.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="field-label">LOCATION</label>
              <select className="control" style={{ width: '100%' }} value={draft.locationCode}
                onChange={(e) => setDraft({ ...draft, locationCode: e.target.value })}>
                <option value="">—</option>
                {reference.facilities.map((f) => <option key={f.code} value={f.code}>{f.name}</option>)}
              </select>
            </div>
            <div>
              <label className="field-label">START FROM AN EXISTING SCHEDULE</label>
              <select className="control" style={{ width: '100%' }} value="" onChange={(e) => e.target.value && copyFrom(Number(e.target.value))}>
                <option value="">Copy from…</option>
                {rows.filter((r) => r.hasSchedule && r.employeeId !== draft.employeeId).map((r) => (
                  <option key={r.employeeId} value={r.employeeId}>{r.displayName} — {r.summary}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="section-label" style={{ marginBottom: 4 }}>WEEKLY SCHEDULE</div>
          <div style={{ fontSize: 11.5, color: 'var(--muted)', marginBottom: 10 }}>
            Leave both times blank for a day the employee is not scheduled — that is a real state, and it produces
            “Worked unscheduled day” rather than an absence, and waits for approval.
          </div>

          <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
            {DAYS.map((day) => {
              const v = draft.days[day];
              const off = !v.start && !v.end;
              return (
                <div key={day} style={{
                  display: 'grid', gridTemplateColumns: '64px 1fr 1fr 1.1fr 70px', gap: 12,
                  alignItems: 'center', padding: '9px 16px', borderBottom: '1px solid var(--rule)',
                }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700 }}>{day}</div>
                  <input className="control" placeholder="Start" value={v.start}
                    onChange={(e) => setDraft({ ...draft, days: { ...draft.days, [day]: { ...v, start: e.target.value } } })} />
                  <input className="control" placeholder="End" value={v.end}
                    onChange={(e) => setDraft({ ...draft, days: { ...draft.days, [day]: { ...v, end: e.target.value } } })} />
                  <div style={{ fontSize: 11.5, color: 'var(--orange)', fontWeight: 600 }}>{off ? 'Not scheduled' : ''}</div>
                  <div style={{ textAlign: 'right' }}>
                    <button className="btn btn--sm" style={{ border: 'none', background: 'transparent', color: 'var(--note)' }}
                      onClick={() => setDraft({ ...draft, days: { ...draft.days, [day]: { start: '', end: '' } } })}>
                      Clear
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 18 }}>
            <button className="btn btn--primary" disabled={save.isPending} onClick={() => save.mutate(draft)}>
              {save.isPending ? 'Saving…' : 'Save schedule'}
            </button>
            <button className="btn" onClick={() => setDraft(null)}>Cancel</button>
            {editing.hasSchedule && (
              <button
                className="btn"
                style={{ color: 'var(--red)' }}
                disabled={removeSchedule.isPending}
                onClick={() => {
                  const msg = `Remove ${editing.displayName}'s schedule entirely?`
                    + `

They will show as "No schedule set" and lateness cannot be judged for them.`
                    + `
Any absence days that existed only because of this schedule are cleared.`;
                  if (window.confirm(msg)) removeSchedule.mutate(editing.employeeId);
                }}
              >
                Remove schedule
              </button>
            )}
            <div className="meta" style={{ marginLeft: 'auto' }}>
              Times accept “7:00 AM” or “07:00”.
            </div>
          </div>
        </div>
      )}

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              {canEdit && (
                <th style={{ width: 28 }}>
                  <input
                    type="checkbox"
                    aria-label="Select all shown"
                    title="Select every employee currently listed"
                    checked={rows.length > 0 && selected.size === rows.length}
                    ref={(el) => { if (el) el.indeterminate = selected.size > 0 && selected.size < rows.length; }}
                    onChange={(e) => setSelected(e.target.checked ? new Set(rows.map((r) => r.employeeId)) : new Set())}
                  />
                </th>
              )}
              <th className="num">ID</th>
              <SortableHeader label="NAME" column="displayName" sort={sort} onSort={toggleSort} />
              <SortableHeader label="DEPARTMENT" column="department" sort={sort} onSort={toggleSort} />
              <SortableHeader label="LOCATION" column="locationCode" sort={sort} onSort={toggleSort} />
              <SortableHeader label="WEEKLY SCHEDULE" column="summary" sort={sort} onSort={toggleSort} />
              <th />
            </tr>
          </thead>
          <tbody>
            {isLoading && <EmptyRow colSpan={7}>Loading roster…</EmptyRow>}
            {!isLoading && rows.length === 0 && <EmptyRow colSpan={7}>No employees match.</EmptyRow>}
            {rows.map((r) => (
              <tr key={r.employeeId}>
                {canEdit && (
                  <td>
                    <input
                      type="checkbox"
                      checked={selected.has(r.employeeId)}
                      onChange={() =>
                        setSelected((prev) => {
                          const next = new Set(prev);
                          next.has(r.employeeId) ? next.delete(r.employeeId) : next.add(r.employeeId);
                          return next;
                        })
                      }
                      aria-label={`Select ${r.displayName}`}
                    />
                  </td>
                )}
                <td className="num">{r.employeeId}</td>
                <td className="strong">
                  {r.displayName}
                </td>
                <td>{r.department ?? '—'}</td>
                <td>{r.locationCode ?? '—'}</td>
                {/* "No schedule set" is a real, common state — many
                    Transportation employees have none, so lateness cannot be
                    judged for them and their points come from the incident
                    log only. It is called out, not hidden. */}
                <td style={{ color: r.hasSchedule ? 'var(--text-secondary)' : 'var(--orange)' }}>{r.summary}</td>
                <td className="num">
                  {canEdit && (
                    <button className="btn btn--sm" style={{ border: 'none', background: 'transparent', color: 'var(--blue)', fontWeight: 700 }}
                      onClick={() => openEdit(r)}>
                      Edit
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

    </div>
  );
}

function pair(d: Draft, day: string) {
  const v = d.days[day];
  return { start: v.start || null, end: v.end || null };
}
